import { SyntheticEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment, { Moment } from "moment";
import QCXDatePicker from "../../../../shared-components/date-picker/QCXDatePicker";
import {
  selectUnidadesDeNegocioAssociadas,
  selectUnidadeSelecionada,
} from "../../../../features/usuario-logado/usuarioLogadoSlice";
import { setErrorFeedback, setSuccessFeedback } from "../../../../features/feedback/feedbackSlice";
import {
  fetchAllAsync as fetchEmpresasEstrangeiras,
  selectEmpresasEstrangeiras,
} from "../../../../features/empresa-estrangeira/empresaEstrangeiraSlice";
import { useListClientes } from "../../../common/hooks/useListClientes";
import SelectStyled from "../../../common/components/input/selectStyled";
import MultiSelectStyled from "../../../common/components/input/multiSelectStyled";
import { GridItemModalProps } from "../../../common/components/cardsGrid/cardsModalGrid";
import generateRelatCatalogoOperadores from "./relatorioCatalogoOperadoresPage.helpers";

const KEYCLOAK_TOKEN_TIMEOUT = 10;

interface ControlledValue {
  id: string | number;
  value: string;
}

interface IUnidadeDeNegocio {
  code: string;
  description: string;
  id: number;
  pessoa: {
    nome: string;
    nomeResumido: string;
  };
  sigla: string;
}

const useStyles = makeStyles({
  root: {
    "& .MuiDialog-paper": {
      minWidth: "52vw",
    },
  },
});

const RelatorioCatalogoOperadoresPage = ({ title, isOpen, closeModal }: GridItemModalProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const { token } = keycloak;
  keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);

  const selectedUnit = useSelector(selectUnidadeSelecionada);
  const businessUnits: IUnidadeDeNegocio[] = useSelector(selectUnidadesDeNegocioAssociadas);
  const empresasEstrangeiras = useSelector(selectEmpresasEstrangeiras);
  const [clientes] = useListClientes();

  const [selectedBusiness, setSelectedBusiness] = useState<ControlledValue | undefined>(undefined);
  const [selectedClients, setSelectedClients] = useState<ControlledValue[]>([]);
  const [selectedEmpresas, setSelectedEmpresas] = useState<ControlledValue[]>([]);
  const [partnumber, setPartnumber] = useState("");
  const [codigoIntegracao, setCodigoIntegracao] = useState("");
  const [dataInicio, setDataInicio] = useState<Moment | null>(null);
  const [dataFim, setDataFim] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchEmpresasEstrangeiras());
  }, [dispatch]);

  const handleBusinessChange = (
    _event: SyntheticEvent,
    value: { id: string | number | undefined; value: string | undefined } | null
  ) => {
    if (value == null) setSelectedBusiness(undefined);
    else setSelectedBusiness({ id: value.id as string, value: value.value as string });
  };

  const handleClientsChange = (_event: SyntheticEvent, value: ControlledValue[] | null) => {
    setSelectedClients(value || []);
  };

  const handleEmpresasChange = (_event: SyntheticEvent, value: ControlledValue[] | null) => {
    setSelectedEmpresas(value || []);
  };

  const tenantId = selectedBusiness ? Number(selectedBusiness.id) : selectedUnit;

  const handleSubmit = async () => {
    if (!tenantId) {
      dispatch(setErrorFeedback({ message: "Selecione uma unidade de negócio." }));
      return;
    }
    if (!token) return;

    setLoading(true);
    keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);

    try {
      const clienteIds = selectedClients.map((c) => String(c.id));
      const empresaEstrangeiraIds = selectedEmpresas.map((e) => String(e.id));
      const error = await generateRelatCatalogoOperadores(
        token,
        tenantId,
        clienteIds,
        partnumber,
        empresaEstrangeiraIds,
        codigoIntegracao,
        dataInicio ? dataInicio.format("YYYY-MM-DD") : undefined,
        dataFim ? dataFim.format("YYYY-MM-DD") : undefined
      );

      if (error) {
        dispatch(setErrorFeedback({ message: error }));
      } else {
        dispatch(setSuccessFeedback({ message: "Relatório gerado com sucesso." }));
      }
    } catch {
      dispatch(setErrorFeedback({ message: "Erro inesperado ao gerar relatório." }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog className={classes.root} open={isOpen} onClose={closeModal}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <SelectStyled
                disablePortal={false}
                controlledValue={selectedBusiness}
                options={businessUnits.map((bu) => ({
                  id: bu.id,
                  value: bu.pessoa.nomeResumido,
                }))}
                onChangeAction={handleBusinessChange}
                label="Unidade de Negócio"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <MultiSelectStyled
                disablePortal={false}
                options={clientes.map((c) => ({
                  id: c.id ?? "",
                  value: `(${c.pessoa?.cnpj ?? ""}) ${c.pessoa?.nome ?? ""}`,
                }))}
                onChangeAction={handleClientsChange}
                label="Clientes"
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <MultiSelectStyled
                disablePortal={false}
                options={empresasEstrangeiras.map((e: any) => {
                  const codOpe = e.relacaoClientes?.[0]?.codigoExportadorSiscomex;
                  const nome = e.pessoaJuridica?.nomeResumido || e.pessoaJuridica?.nome || String(e.id);
                  return {
                    id: e.id,
                    value: codOpe ? `${codOpe} - ${nome}` : nome,
                  };
                })}
                onChangeAction={handleEmpresasChange}
                label="Operador Estrangeiro"
              />
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Part Number"
              value={partnumber}
              onChange={(e) => setPartnumber(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Código de Integração"
              value={codigoIntegracao}
              onChange={(e) => setCodigoIntegracao(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker
              label="Data de Início"
              value={dataInicio}
              onChange={setDataInicio}
            />
          </Grid>

          <Grid item xs={6}>
            <QCXDatePicker
              label="Data de Fim"
              value={dataFim}
              onChange={setDataFim}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box padding={2} display="flex" gridGap={8}>
          <Button variant="outlined" color="primary" onClick={closeModal} disabled={loading}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={loading || !tenantId}
          >
            {loading ? "Gerando..." : "Gerar Relatório"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RelatorioCatalogoOperadoresPage;
